<template>
  <div class="tab-content">
    <div class="form-group">
      <label class="label">{{ $t('notifications_modal.notify_users') }}</label>
      <div class="notification-options">
        <b-checkbox
          v-model="watchersSelected"
          :disabled="watchers.length === 0"
        >
          {{ $t('notifications_modal.watchers') }} ({{ watchers.length }})
        </b-checkbox>
        <b-checkbox
          v-model="csmSelected"
          :disabled="!isCsmNotificationEnabled || csmUsers.length === 0"
        >
          {{ $t('notifications_modal.csm') }}
          <span v-if="csmUsers.length > 0" class="role-users-list">
            ({{ csmUsersDisplay }})
          </span>
        </b-checkbox>
        <b-checkbox
          v-model="repairEngineerSelected"
          :disabled="
            !isRepairEngineerNotificationEnabled ||
            repairEngineerUsers.length === 0
          "
        >
          {{ $t('notifications_modal.repair_engineer') }}
          <span v-if="repairEngineerUsers.length > 0" class="role-users-list">
            ({{ repairEngineerUsersDisplay }})
          </span>
        </b-checkbox>
        <b-checkbox
          v-model="supervisorSelected"
          :disabled="
            !isSupervisorNotificationEnabled || supervisorUsers.length === 0
          "
        >
          {{ $t('notifications_modal.supervisor') }}
          <span v-if="supervisorUsers.length > 0" class="role-users-list">
            ({{ supervisorUsersDisplay }})
          </span>
        </b-checkbox>
        <b-checkbox
          v-model="projectOwnerSelected"
          :disabled="!isProjectOwnerNotificationEnabled"
        >
          {{ $t('notifications_modal.project_owner') }}
          <span v-if="projectOwnerUsers.length > 0" class="role-users-list">
            ({{ projectOwnerUsersDisplay }})
          </span>
        </b-checkbox>
      </div>
    </div>

    <div class="form-group">
      <label class="label">{{ $t('notifications_modal.recipients') }}</label>
      <div class="recipients-container">
        <div v-if="isRecipientPickerVisible" class="recipient-picker">
          <b-autocomplete
            ref="specificRecipientAutocomplete"
            :value="specificRecipientSearch"
            :data="availableUsers"
            :placeholder="$t('notifications_modal.search_users')"
            icon="magnify"
            :open-on-focus="true"
            clearable
            @input="updateSpecificRecipientSearch"
            @select="onSelectSpecificRecipient"
          >
            <template slot-scope="props">
              <div class="user-option">
                <span class="user-name">{{
                  props.option.fullname || props.option._id
                }}</span>
                <span class="user-email">{{ props.option._id }}</span>
              </div>
            </template>
          </b-autocomplete>
        </div>
        <div class="recipients-list">
          <div
            v-for="recipient in selectedRecipients"
            :key="recipient._id"
            class="recipient-tag"
          >
            <span class="recipient-text">
              {{ recipient.fullname || recipient._id }}
            </span>
            <button
              type="button"
              class="recipient-remove-btn"
              @click="excludeRecipient(recipient._id)"
              :title="$t('notifications_modal.exclude_recipient')"
            >
              ✕
            </button>
          </div>
          <button
            type="button"
            class="recipient-tag recipient-add-tag"
            @click="toggleRecipientPicker"
            :title="$t('notifications_modal.add_recipient')"
          >
            <span class="recipient-add-icon">+</span>
          </button>
        </div>
      </div>
    </div>

    <div class="form-group">
      <label class="label">{{ $t('notifications_modal.template') }}</label>
      <b-field>
        <b-select
          :value="selectedTemplateId"
          expanded
          :loading="isLoadingTemplates"
          @input="updateTemplate"
        >
          <option :value="''">
            {{ $t('notifications_modal.no_template') }}
          </option>
          <option
            v-for="template in templates"
            :key="template._id"
            :value="template._id"
          >
            {{ template.name }}
          </option>
        </b-select>
      </b-field>
    </div>

    <div class="form-group">
      <label class="label">{{
        $t('notifications_modal.notification_message')
      }}</label>
      <rich-text-editor
        ref="notificationMessageEditor"
        :value="notificationMessage"
        :headings="[1, 2, 3]"
        :placeholder="$t('type_message_placeholder')"
        @input="updateMessage"
      />

      <b-field :label="$t('notifications_modal.available_placeholders')">
        <div class="notification-placeholders">
          <b-tag
            v-for="placeholder in availablePlaceholders"
            :key="placeholder"
            type="is-light"
            class="notification-placeholder-tag"
            tabindex="0"
            role="button"
            @click.native="insertPlaceholder(placeholder)"
            @keydown.enter.native.prevent="insertPlaceholder(placeholder)"
          >
            {{ placeholder }}
          </b-tag>
        </div>
      </b-field>
    </div>
  </div>
</template>

<script>
import RichTextEditor from '../../RichTextEditor';

export default {
  name: 'send-notification-tab',
  components: {
    RichTextEditor,
  },
  props: {
    currentJob: {
      type: Object,
      required: true,
    },
    watchers: {
      type: Array,
      required: true,
    },
    users: {
      type: Array,
      required: true,
    },
    notificationRecipients: {
      type: Object,
      required: true,
    },
    notificationMessage: {
      type: String,
      default: '',
    },
    templates: {
      type: Array,
      default: () => [],
    },
    selectedTemplateId: {
      type: String,
      default: '',
    },
    isLoadingTemplates: {
      type: Boolean,
      default: false,
    },
    excludedRecipients: {
      type: Array,
      default: () => [],
    },
    specificRecipients: {
      type: Array,
      default: () => [],
    },
    sites: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      specificRecipientSearch: '',
      isRecipientPickerVisible: false,
      availablePlaceholders: [
        '{{jobTitle}}',
        '{{jobLink}}',
        '{{recipientName}}',
        '{{projectInfoText}}',
        '{{workOrderNumber}}',
        '{{locationName}}',
        '{{taskLink}}',
        '{{senderFullname}}',
        '{{senderEmail}}',
      ],
    };
  },
  computed: {
    csmUsers() {
      if (!this.currentJob || !Array.isArray(this.currentJob.csmUser)) {
        return [];
      }
      return this.currentJob.csmUser.map((csm) => ({
        _id: csm.id || csm._id,
        fullname: csm.name || csm.fullname,
      }));
    },
    repairEngineerUsers() {
      if (
        !this.currentJob ||
        !Array.isArray(this.currentJob.repairEngineerUser)
      ) {
        return [];
      }
      return this.currentJob.repairEngineerUser.map((re) => ({
        _id: re.id || re._id,
        fullname: re.name || re.fullname,
      }));
    },
    supervisorUsers() {
      const usersFromJobLocations = Array.isArray(this.users) ? this.users : [];
      const currentJobSiteId = this.normalizeSiteId(this.getCurrentJobSiteId());
      const supervisorsFromLocations = usersFromJobLocations
        .filter(
          (user) =>
            this.isSupervisorRole(user) &&
            this.isPrimaryLocationMatch(user, currentJobSiteId)
        )
        .map((user) => ({
          _id: user._id || user.id,
          fullname: user.fullname || user.name || user._id || user.id,
        }))
        .filter((user) => user && user._id);

      if (usersFromJobLocations.length > 0) {
        return supervisorsFromLocations;
      }

      if (!this.currentJob || !Array.isArray(this.currentJob.supervisorUser)) {
        return [];
      }

      return this.currentJob.supervisorUser
        .map((sup) => ({
          _id: sup.id || sup._id,
          fullname: sup.name || sup.fullname,
        }))
        .filter((sup) => sup && sup._id);
    },
    projectOwnerUsers() {
      if (!this.currentJob) {
        return [];
      }

      if (Array.isArray(this.currentJob.projectOwnerUser)) {
        return this.currentJob.projectOwnerUser
          .map((po) => ({
            _id: po.id || po._id,
            fullname: po.name || po.fullname,
          }))
          .filter((po) => po && po._id);
      }

      if (this.currentJob.prodCreatedByUser) {
        const owner = this.currentJob.prodCreatedByUser;
        const ownerId = owner._id || owner.email || owner.id;
        if (!ownerId) {
          return [];
        }

        return [
          {
            _id: ownerId,
            fullname: owner.fullname || owner.name || ownerId,
          },
        ];
      }

      return [];
    },
    csmUsersDisplay() {
      return this.csmUsers.map((u) => u.fullname).join(', ');
    },
    repairEngineerUsersDisplay() {
      return this.repairEngineerUsers.map((u) => u.fullname).join(', ');
    },
    supervisorUsersDisplay() {
      return this.supervisorUsers.map((u) => u.fullname).join(', ');
    },
    projectOwnerUsersDisplay() {
      return this.projectOwnerUsers.map((u) => u.fullname).join(', ');
    },
    selectedRecipients() {
      const recipients = [];

      if (this.notificationRecipients.watchers) {
        recipients.push(...this.watchers);
      }

      if (this.notificationRecipients.csm) {
        recipients.push(...this.csmUsers);
      }

      if (this.notificationRecipients.repairEngineer) {
        recipients.push(...this.repairEngineerUsers);
      }

      if (this.notificationRecipients.supervisor) {
        recipients.push(...this.supervisorUsers);
      }

      if (this.notificationRecipients.projectOwner) {
        recipients.push(...this.projectOwnerUsers);
      }

      recipients.push(...this.specificRecipients);

      const uniqueRecipients = Array.from(
        new Map(recipients.map((r) => [r._id, r])).values()
      );

      return uniqueRecipients.filter(
        (r) => !this.excludedRecipients.includes(r._id)
      );
    },
    csmSelected: {
      get() {
        return this.notificationRecipients.csm;
      },
      set(value) {
        this.updateRecipientsField('csm', value);
      },
    },
    repairEngineerSelected: {
      get() {
        return this.notificationRecipients.repairEngineer;
      },
      set(value) {
        this.updateRecipientsField('repairEngineer', value);
      },
    },
    supervisorSelected: {
      get() {
        return this.notificationRecipients.supervisor;
      },
      set(value) {
        this.updateRecipientsField('supervisor', value);
      },
    },
    projectOwnerSelected: {
      get() {
        return this.notificationRecipients.projectOwner;
      },
      set(value) {
        this.updateRecipientsField('projectOwner', value);
      },
    },
    isCsmAutoIncluded() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId || !this.sites) {
        return false;
      }
      const currentSite = this.sites.find((s) => s._id === jobSiteId);
      return currentSite?.csmNotificationsEnabled === true;
    },
    isRepairEngineerAutoIncluded() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId || !this.sites) {
        return false;
      }
      const currentSite = this.sites.find((s) => s._id === jobSiteId);
      return currentSite?.repairEngineerNotificationsEnabled === true;
    },
    isSupervisorAutoIncluded() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId || !this.sites) {
        return false;
      }
      const currentSite = this.sites.find((s) => s._id === jobSiteId);
      return currentSite?.supervisorNotificationsEnabled === true;
    },
    isProjectOwnerAutoIncluded() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId || !this.sites) {
        return false;
      }
      const currentSite = this.sites.find((s) => s._id === jobSiteId);
      return currentSite?.projectOwnerNotificationsEnabled === true;
    },
    isCsmNotificationEnabled() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId || !this.sites) {
        return false;
      }
      const currentSite = this.sites.find((s) => s._id === jobSiteId);
      return (
        currentSite?.csmNotificationsEnabled === true ||
        currentSite?.allRoleNotificationsEnabled === true
      );
    },
    isRepairEngineerNotificationEnabled() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId || !this.sites) {
        return false;
      }
      const currentSite = this.sites.find((s) => s._id === jobSiteId);
      return (
        currentSite?.repairEngineerNotificationsEnabled === true ||
        currentSite?.allRoleNotificationsEnabled === true
      );
    },
    isSupervisorNotificationEnabled() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId || !this.sites) {
        return false;
      }
      const currentSite = this.sites.find((s) => s._id === jobSiteId);
      return (
        currentSite?.supervisorNotificationsEnabled === true ||
        currentSite?.allRoleNotificationsEnabled === true
      );
    },
    isProjectOwnerNotificationEnabled() {
      const jobSiteId = this.getCurrentJobSiteId();

      if (!jobSiteId || !this.sites) {
        return false;
      }
      const currentSite = this.sites.find((s) => s._id === jobSiteId);
      return (
        currentSite?.projectOwnerNotificationsEnabled === true ||
        currentSite?.allRoleNotificationsEnabled === true
      );
    },
    availableUsers() {
      const search = (this.specificRecipientSearch || '').toLowerCase().trim();
      const selectedIds = new Set(this.specificRecipients.map((r) => r._id));

      let filtered = this.users.filter((user) => !selectedIds.has(user._id));

      if (search) {
        filtered = filtered.filter((user) => {
          const fullname = (user.fullname || '').toLowerCase();
          const email = (user._id || '').toLowerCase();
          return fullname.includes(search) || email.includes(search);
        });
      }

      return filtered;
    },
    watchersSelected: {
      get() {
        return this.notificationRecipients.watchers;
      },
      set(value) {
        this.updateRecipientsField('watchers', value);
      },
    },
  },
  methods: {
    normalizeSiteId(siteLike) {
      if (!siteLike) {
        return null;
      }

      if (typeof siteLike === 'string') {
        const normalized = siteLike.trim();
        return normalized || null;
      }

      if (typeof siteLike === 'object') {
        return String(siteLike._id || siteLike.id || '').trim() || null;
      }

      return String(siteLike).trim() || null;
    },
    isPrimaryLocationMatch(user, jobSiteId) {
      if (!jobSiteId) {
        return false;
      }

      const primaryLocationId = this.normalizeSiteId(user?.location);
      return primaryLocationId === jobSiteId;
    },
    isSupervisorRole(user) {
      const normalizedRoleName = String(user?.role || '')
        .toLowerCase()
        .trim()
        .replace(/[-_\s]/g, '');

      return normalizedRoleName === 'supervisor';
    },
    getCurrentJobSiteId() {
      if (this.currentJob?.location) {
        return this.currentJob.location;
      }

      if (this.currentJob?.executingSite) {
        return this.currentJob.executingSite;
      }

      if (
        !Array.isArray(this.currentJob?.sites) ||
        this.currentJob.sites.length === 0
      ) {
        return null;
      }

      const [firstSite] = this.currentJob.sites;
      if (typeof firstSite === 'string') {
        return firstSite;
      }

      return firstSite?._id || firstSite?.id || null;
    },
    updateRecipientsField(field, value) {
      this.$emit('update:notificationRecipients', {
        ...this.notificationRecipients,
        [field]: value,
      });
    },
    updateMessage(value) {
      this.$emit('update:notificationMessage', value);
    },
    insertPlaceholder(placeholder) {
      this.$refs.notificationMessageEditor?.insertTextAtCursor(placeholder);
    },
    updateTemplate(value) {
      this.$emit('update:selectedTemplateId', value);

      const selectedTemplate = this.templates.find(
        (template) => template._id === value
      );
      if (selectedTemplate) {
        this.$emit(
          'update:notificationMessage',
          selectedTemplate.bodyTemplate || ''
        );
      }
    },
    excludeRecipient(recipientId) {
      const updated = [...this.excludedRecipients, recipientId];
      this.$emit('update:excludedRecipients', updated);
    },
    includeRecipient(recipientId) {
      const updated = this.excludedRecipients.filter(
        (id) => id !== recipientId
      );
      this.$emit('update:excludedRecipients', updated);
    },
    updateSpecificRecipientSearch(value) {
      this.specificRecipientSearch = value;
    },
    toggleRecipientPicker() {
      if (this.isRecipientPickerVisible) {
        this.closeRecipientPicker();
        return;
      }

      this.openRecipientPicker();
    },
    openRecipientPicker() {
      this.isRecipientPickerVisible = true;
      this.$nextTick(() => {
        const root = this.$refs.specificRecipientAutocomplete?.$el;
        const input = root?.querySelector('input');
        if (input) {
          input.focus();
        }
      });
    },
    closeRecipientPicker() {
      this.isRecipientPickerVisible = false;
      this.specificRecipientSearch = '';
    },
    onSelectSpecificRecipient(user) {
      if (user && !this.specificRecipients.find((r) => r._id === user._id)) {
        this.$emit('update:specificRecipients', [
          ...this.specificRecipients,
          user,
        ]);
        this.specificRecipientSearch = '';
        this.closeRecipientPicker();
      }
    },
    removeSpecificRecipient(userId) {
      this.$emit(
        'update:specificRecipients',
        this.specificRecipients.filter((r) => r._id !== userId)
      );
    },
  },
};
</script>

<style scoped>
.tab-content {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #363636;
}

.notification-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.recipients-container {
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
  padding: 8px;
}

.recipient-picker {
  margin-bottom: 8px;
}

.recipient-add-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 24px;
  padding: 0;
  gap: 0;
  cursor: pointer;
  border-style: dashed;
  color: #3273dc;
  background: #f5f9ff;
}

.recipient-add-tag:hover {
  border-color: #3273dc;
  background: #edf4ff;
}

.recipient-add-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1;
  width: 100%;
  transform: translateY(-1px);
}

.recipients-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.recipient-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: white;
  border: 1px solid #dbdbdb;
  border-radius: 16px;
  padding: 3px 8px;
  font-size: 0.85rem;
}

.recipient-text {
  color: #363636;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recipient-remove-btn {
  background: none;
  border: none;
  color: #b3b3b3;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
  line-height: 1;
  transition: color 0.2s ease;
  margin-left: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.recipient-remove-btn:hover {
  color: #f14668;
}

.user-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  font-weight: 600;
  color: #363636;
}

.user-email {
  font-size: 0.8rem;
  color: #7a7a7a;
}

.specific-recipients-list {
  margin-top: 10px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
  padding: 10px;
}

.specific-recipient-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: white;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin-bottom: 6px;
}

.specific-recipient-item:last-child {
  margin-bottom: 0;
}

.auto-included-badge {
  font-size: 0.75rem;
  color: #7a7a7a;
  font-style: italic;
  margin-left: 4px;
}

.disabled-badge {
  font-size: 0.75rem;
  color: #f14668;
  font-style: italic;
  margin-left: 4px;
}

.role-users-list {
  color: #7a7a7a;
  font-size: 0.95rem;
  font-weight: normal;
}

.recipient-name {
  font-weight: 600;
  color: #363636;
  flex: 1;
}

.recipient-email {
  font-size: 0.85rem;
  color: #7a7a7a;
  flex: 1;
}

.remove-btn {
  background: none;
  border: none;
  color: #b3b3b3;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 4px;
  line-height: 1;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.remove-btn:hover {
  color: #f14668;
}

.notification-placeholders {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.notification-placeholder-tag {
  font-family: monospace;
  cursor: pointer;
}
</style>
